// Doit rester le premier import : résout le mode de lancement avant tout autre module.
import './initialisationModeLancement';
import { Icon, Tooltip } from '@mairistem/composants';
import { App, Feature, Menu, render } from '@mairistem/module';
import { request } from '@mairistem/tools';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import 'dayjs/locale/fr';
import _ from 'lodash';
import * as React from 'react';
import ReactDOM from 'react-dom';
import { MenuAccueil } from './components/Accueil';
import MenuArretes from './components/Arrete/MenuArretes';
import { MenuConstitution } from './components/ConstitutionRegie';
import { Context } from './components/Context';
import { EncaissementTermineMessage } from './components/Context/EmbeddedHandler';
import { MenuEncaissement } from './components/Encaissement';
import { MenuEncaissementDirect } from './components/EncaissementDirect';
import { MenuImpayes } from './components/Impayes';
import { MenuControles } from './components/MenuControles';
import { MenuListes } from './components/MenuListes';
import { MenuRecus } from './components/MenuRecus';
import { MenuParametrage } from './components/Parametrage';
import { FeaturePayeurFiche, FeaturePayeurListe } from './components/Payeur';
import { MenuPrelevements } from './components/Prelevement';
import { MenuVersementTresor } from './components/Versement';
import { MENUS } from './constants';
import { initialize } from './sentry';
import { showTooltips } from './utils/menu';
import { estEncaissementDirect, estEncaissementTermine } from './utils/modeLancement';

import './index.less';
import './components/Accueil/v2/Accueil.less';

dayjs.extend(customParseFormat);
dayjs.locale('fr');
initialize();

// Evite que module tape sur la régie /api/auth en mode embedded
request.interceptors.request.use((config) => {
  if (process.env.NODE_ENV === 'production') {
    if (_.includes(window.location.href, '.jvsonline.fr')) {
      config.baseURL = 'https://360.jvsonline.fr';
    } else if (_.includes(window.location.href, '.qa.')) {
      config.baseURL = 'https://360.qa.jvs-mairistem.dev';
    } else if (_.includes(window.location.href, '.pp.')) {
      config.baseURL = 'https://360.pp.jvs-mairistem.dev';
    } else {
      config.baseURL = 'https://360.jvs-mairistem.dev';
    }
  }

  return config;
});

/** Overlay qui affiche "Encaissement terminé" si done === true dans sessionStorage */
const EmbeddedDoneOverlay = () => {
  if (!estEncaissementTermine()) {
    return null;
  }

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'white',
        zIndex: 9999,
      }}
    >
      <EncaissementTermineMessage />
    </div>
  );
};

const Regie = () => {
  const code = '360_REGI';

  // Ne pas modifier ou supprimer
  // Utile en mode production afin que le style se place au bonne endroit
  if (process.env.NODE_ENV !== 'development') {
    const oldCreatePortal = ReactDOM.createPortal;

    ReactDOM.createPortal = (child, domNode) =>
      oldCreatePortal(
        child,
        domNode === document.body
          ? document.querySelector(`#${CSS.escape(code)}`) || domNode
          : domNode || document.querySelector(`#${CSS.escape(code)}`),
      );
  }

  // Mode de lancement résolu au chargement (cf. initialisationModeLancement) :
  // en encaissement direct (LOOC), l'onglet est cloisonné à /encaissement-direct.
  const embedded = estEncaissementDirect();

  // onAuth reste branché pour respecter le contrat du composant App de @mairistem/module.
  const handleAuth = React.useCallback(() => {}, []);

  const menus = React.useMemo(() => {
    // En encaissement direct, seul ce menu est déclaré : aucune autre route n'existe
    if (embedded) {
      return <MenuEncaissementDirect />;
    }

    return (
      <>
        <MenuAccueil />
        <MenuParametrage />
        <Menu.Separator />
        <MenuEncaissement />
        <Menu
          code={MENUS.MENU_PAYEUR}
          name="Payeurs"
          icon={
            showTooltips ? (
              <Tooltip trigger={<Icon name="users" />} message="Payeurs" position="right center" />
            ) : (
              <Icon name="users" />
            )
          }
          path="/payeurs"
        >
          <Feature path="/:identifiant">
            <Feature.Content>
              <FeaturePayeurFiche />
            </Feature.Content>
          </Feature>
          <Feature>
            <Feature.Content>
              <FeaturePayeurListe />
            </Feature.Content>
          </Feature>
        </Menu>
        <Menu
          code={MENUS.MENU_CONSTITUTION}
          name="Constitution de la régie"
          icon={
            showTooltips ? (
              <Tooltip
                trigger={<Icon name="building" />}
                message="Constitution de la régie"
                position="right center"
              />
            ) : (
              <Icon name="building" />
            )
          }
          path="/constitution/:identifiant"
        >
          <Feature>
            <Feature.Content>
              <MenuConstitution />
            </Feature.Content>
          </Feature>
        </Menu>
        <MenuListes />
        <MenuRecus />
        <MenuPrelevements />
        <MenuArretes />
        <MenuVersementTresor />
        <MenuImpayes />
        <MenuControles />
      </>
    );
  }, [embedded]);

  return (
    <App
      name="Régie de recettes"
      code={code}
      group="facturation"
      context={Context}
      request={request}
      onAuth={handleAuth}
      {...(embedded && { embedded: true })}
    >
      <EmbeddedDoneOverlay />
      {menus}
    </App>
  );
};

render(<Regie />);
